/* eslint-disable max-classes-per-file */
import {
    observable, reaction, computed, makeObservable, action,
} from 'mobx'
import { createTransformer } from 'mobx-utils'
import i18n from 'i18next'
import * as R from 'ramda'
import PageQuestionAPI from '../API/PageQuestion'
import { unescapeHtmlTags } from '../utils/helpers'
import {
    CountryListQuestion,
    DropdownListQuestion,
    GridQuestion,
    MultipleChoiceQuestion,
    NumericQuestion,
    PercentageQuestion,
    PriorityQuestion,
    RatingQuestion,
    SingleChoiceQuestion,
    SmartGridQuestion,
    StaticText,
    TextAreaQuestion,
    TextBoxQuestion,
    OptionSet,
    UrlVariableQuestion,
    TextBoxListQuestion,
    DateQuestion,
    FormQuestion,
    NumberFormQuestion,
} from './Question'
import SkipLogic from './SkipLogic'
import withRollback from '../utils/withRollback'

class ExploriMap {
    constructor(entries = []) {
        this.map = new Map(entries)
    }

    set(key, value) {
        this.map.set(key, value instanceof LangText ? value : new LangText(value))
    }

    has(value) {
        return this.map.has(value)
    }

    get(value) {
        return this.map.get(value)
    }

    entries() {
        return this.map.entries()
    }

    toJSON() {
        const json = {}
        this.map.forEach((value, key) => {
            console.log(`ExploriMap toJSON ${key} ${JSON.stringify(value)}`)
            json[key] = value instanceof Object ? value.toJSON() : value
        })
        return json
    }
}

class LangText {
    constructor({ locale, text }) {
        this.locale = locale
        this.text = text
    }

    toJSON() {
        return {
            locale: this.locale,
            text: this.text,
        }
    }
}

const surveyPageTypes = {
    ExitPage: 'EX',
    CompletionPage: 'CO',
    QuotaFullPage: 'QF',
    StandardPage: 'ST',
}

const inheritsAllowedFrom = ['RD', 'CH', 'SB']

const multiQuestions = ['RD', 'CH', 'TB', 'OD', 'PC', 'RT', 'SB']

class PageQuestion {
    @observable isEditable = false

    @observable modifiable = false

    @observable deletable = false

    @observable inheritVisible = false

    @observable isMandatory

    @observable isHidden

    @observable isLocked

    @observable parentId

    @observable question

    @observable questionLevel

    @observable copying = false

    @observable skipLogic

    @observable hasPopulateFrom = false

    @observable canBeEdited

    @observable isAiCodingDisabled = false

    @computed get questionTitle() {
        return createTransformer((store) => {
            const { survey, surveyLocale } = store
            const { defaultLocale } = survey.metadata
            let title = ''
            if (this.question) {
                const { initialLocale } = this.question.metadata
                if (this.question.content.shortTitle.has(surveyLocale)) {
                    title = this.question.content.shortTitle.get(surveyLocale).text
                } else if (this.question.content.shortTitle.has(defaultLocale)) {
                    title = this.question.content.shortTitle.get(defaultLocale).text
                } else if (this.question.content.shortTitle.has(initialLocale)) {
                    title = this.question.content.shortTitle.get(initialLocale).text
                }
            } else if (this.copying) {
                title = 'copying'
            }
            return unescapeHtmlTags(title)
        })
    }

    @computed get previousPageQuestions() {
        return createTransformer(({ store, currentPriority }) => {
            return store.survey.pages.filter((page) => page.priority < currentPriority)
                .reduce((obj, page) => (
                    obj.concat(page.questions.reduce((prevQ, pq) => {
                        if (pq.isInheritable()) {
                            prevQ.push({
                                value: pq.id,
                                qid: pq.questionId,
                                label: `${i18n.t('page')}: ${page.priority} - ${pq.questionTitle(store)}`,
                            })
                        }
                        return prevQ
                    }, []))
                ), [])
        })
    }

    @computed get qlistRecommended() {
        return this.question ? this.question.qlistRecommended : false
    }

    @computed get kpiType() {
        return this.question ? this.question.kpiType : false
    }

    @computed get hasSkipLogic() {
        return createTransformer((store) => this.findSkipLogic(store))
    }

    @computed get hasOptionSkipLogic() {
        return R.or(this.skipLogic, []).filter((sl) => (sl.action.kind === 'ShowQuestionOption')).length > 0
    }

    @computed get hasValidPopulateFrom() {
        return createTransformer((survey) => {
            if (!this.hasPopulateFrom) {
                return false
            }
            const pageIndex = this.getPageArrIndex(survey)
            const pages = survey.pages.filter((p, i) => p.pageType === 'ST' && i <= pageIndex)
            const currentPage = pages[pageIndex]
            const currentQuestionIndex = currentPage.questions.findIndex((pq) => pq.id === this.id)
            console.log(pageIndex, currentQuestionIndex)
            console.log(this.id)
            console.log(JSON.stringify(currentPage.questions, null, 4))
            const sourcePageQuestionId = this.populateFrom.pageQuestionId
            const pageQuestions = pages.reduce((arr, page, i) => (
                arr.concat(
                    page.questions
                        .filter((q, questionIndex) => (
                            !['FM', 'ST'].includes(R.pathOr('', ['question', 'questionTypeCode'], q))
                            && !q.isHidden
                            && (
                                questionIndex < currentQuestionIndex
                                || i < pageIndex
                            )
                        ))
                        .map(({ id }) => id),
                )
            ), [])
            console.log(`hasValidPopulateFrom ${sourcePageQuestionId} -> ${pageQuestions}`)
            return pageQuestions.includes(sourcePageQuestionId)
        })
    }

    constructor({
        id,
        questionId,
        priority,
        isMandatory = false,
        isHidden = false,
        isLocked = false,
        parentId,
        hasPrepopulatedData = false,
        questionLevel,
        optionSetY,
        skipLogic,
        populateFrom,
        isRecommended,
        isDataImport,
        qlistInherit,
        isEditable = true,
        isAiCodingDisabled,
    }) {
        makeObservable(this)
        this.id = id
        this.questionId = questionId
        this.priority = priority
        this.isHidden = isHidden
        this.isMandatory = isMandatory
        this.isLocked = isLocked
        this.parentId = parentId
        this.hasPrepopulatedData = hasPrepopulatedData
        this.populateFrom = populateFrom
        this.questionLevel = questionLevel
        this.hasPopulateFrom = !!populateFrom
        this.isRecommended = isRecommended
        this.isDataImport = isDataImport
        this.qlistInherit = qlistInherit
        this.canBeEdited = isEditable
        this.isAiCodingDisabled = isAiCodingDisabled

        if (optionSetY) {
            try {
                console.log(optionSetY)
                console.log(new OptionSet(optionSetY))
            } catch (e) {
                console.log(e)
            }
            this.optionSetY = optionSetY instanceof OptionSet ? optionSetY : new OptionSet(optionSetY)
        }

        this.currentOptions = this.getCurrentOptions()
        if (this.parentId) { // && this.parentId !== '00000000-0000-0000-0000-000000000000'
            this.inheritVisible = true
        }

        this.skipLogic = Array.isArray(skipLogic)
            ? skipLogic.map((logic) => (logic instanceof SkipLogic
                ? logic : new SkipLogic(logic))) : []
        reaction(
            () => this.getCurrentOptions(),
            (currentOptions) => this.update(currentOptions),
        )
    }

    clone() {
        return new PageQuestion(this.toJSON())
    }

    isMulti() {
        return multiQuestions.indexOf(this.question.questionTypeCode) > -1
    }

    isInheritable() {
        return this.question && inheritsAllowedFrom.indexOf(this.question.questionTypeCode) > -1
    }

    questionType = () => (
        this.question.questionTypeCode
    )

    toLowerCase(value) {
        return typeof value === 'string' ? value.toLowerCase() : value
    }

    getParentPageIndex(survey) {
        console.log('getParentPageIndex')
        return survey.pages.reduce((pageIndex, page) => {
            let newIndex = -1
            if (page.questions) {
                if (page.questions.find((pq) => this.toLowerCase(pq.id) === this.toLowerCase(this.parentId))) {
                    newIndex = page.priority
                    console.log(newIndex, this.toLowerCase(this.parentId), page.id)
                }
            }
            return newIndex !== -1 ? newIndex : pageIndex
        }, -1)
    }

    getPageIndex(survey) {
        const currentPage = this.id ? survey.pages.find((page) => (
            page.questions
                && page.questions.some((pq) => (pq.id ? (this.toLowerCase(pq.id) === this.toLowerCase(this.id)) : false))
        )) : null
        return currentPage ? currentPage.priority : -1
    }

    getPageArrIndex(survey) {
        const index = this.id ? survey.pages.findIndex((page) => (
            page.questions
                && page.questions.some((pq) => (pq.id ? (this.toLowerCase(pq.id) === this.toLowerCase(this.id)) : false))
        )) : -1
        return index
    }

    getQuestionIndex(survey) {
        const currentPage = survey.pages.find((page) => (
            page.questions
                && page.questions.some((pq) => (pq.id ? (this.toLowerCase(pq.id) === this.toLowerCase(this.id)) : false))
        ))
        return currentPage.questions.findIndex((pq) => pq.id === this.id)
    }

    hasInheringChildrenAffected(survey, targetPagePriority) {
        return survey.pages.reduce((hasChildren, page) => {
            let pageChildren = false
            if (page.questions && page.priority <= targetPagePriority) {
                pageChildren = page.questions
                    .reduce((hasParent, pq) => (
                        hasParent
                            || this.toLowerCase(pq.parentId) === this.toLowerCase(this.id)
                    ), false)
            }
            return hasChildren || pageChildren
        }, false)
    }

    isInheritValid(store) {
        return !this.parentId
            || this.parentId.match(/^[0|-]*$/)
            || this.getParentPageIndex(store.survey) < this.getPageIndex(store.survey)
    }

    isSkipLogicvalid(store, skipLogic) {
        console.log(`isSkipLogicvalid ${this.id} ${this.copying}`)
        let isValid = true
        if (skipLogic && this.question) {
            console.log(`isSkipLogicvalid ${this.id} ${this.copying} ${this.question.id}`)
            console.log(this.priority)
            const pagePriority = this.getPageIndex(store.survey)
            const questionIndex = this.getQuestionIndex(store.survey)
            const pageQuestions = new Map()
            store.survey.pages.forEach((page) => {
                if (page.questions && page.priority <= pagePriority) {
                    page.questions.forEach((question, index) => {
                        if (page.priority < pagePriority || index < questionIndex) {
                            pageQuestions.set(question.id, question)
                        }
                    })
                }
            })
            console.log(JSON.stringify(pageQuestions, null, 4))
            skipLogic.triggerGroups.forEach((triggerGroup) => {
                if (triggerGroup.children) {
                    triggerGroup.children.forEach((child) => {
                        if (child.triggers) {
                            child.triggers.forEach((trigger) => {
                                if ((trigger.condition.inheritedPageQuestionId && pageQuestions.has(trigger.condition.inheritedPageQuestionId))) {
                                    console.log(`bad skiplogic ${JSON.stringify(skipLogic, null, 4)}`)
                                }
                                isValid = isValid
                                    && (
                                        trigger.condition.kind === 'Quota'
                                        || trigger.condition.kind === 'Date'
                                        || (
                                            trigger.condition.kind === 'QuestionExistsInSurveyResponse'
                                            && pageQuestions.has(trigger.condition.pageQuestionId)
                                        )
                                        || (
                                            trigger.condition.kind === 'QuestionHasNumericResponse'
                                            && pageQuestions.has(trigger.condition.pageQuestionId)
                                        )
                                        || (
                                            pageQuestions.has(trigger.condition.pageQuestionId)
                                            && (
                                                !trigger.condition.inheritedPageQuestionId
                                                || pageQuestions.has(trigger.condition.inheritedPageQuestionId)
                                            )
                                            && (
                                                trigger.condition.inheritedPageQuestionId
                                                    ? pageQuestions.get(trigger.condition.inheritedPageQuestionId).question.hasOptions(trigger.condition.set1, trigger.condition.set2)
                                                    : pageQuestions.get(trigger.condition.pageQuestionId).question.hasOptions(trigger.condition.set1, trigger.condition.set2, pageQuestions.get(trigger.condition.pageQuestionId))
                                            )
                                        )
                                    )
                            })
                        }
                    })
                }
            })
            if (!isValid) {
                console.log('invalid skiplogic')
                console.log(JSON.stringify(skipLogic, null, 4))
            }
        }
        return isValid
    }

    findSkipLogic(store) {
        const skipLogic = store.survey.skipLogic.find((logic) => (
            (logic.action.kind === 'ShowQuestion' || logic.action.kind === 'HideQuestion') && logic.action.pageQuestionId === this.id))
        return skipLogic
    }

    filterSkipLogic(store) {
        console.log(`findfilterSkipLogicSkipLogic ${this.id}`)
        return store.survey.skipLogic.filter((logic) => (
            logic.action.pageQuestionId === this.id
            || (
                logic.action.kind === 'SkipToPage'
                && (
                    (logic.trigger && logic.trigger.condition.pageQuestionId === this.id)
                    || (logic.triggerGroups[0] && logic.triggerGroups[0].children[0].triggers[0].condition.pageQuestionId == this.id)
                )
            )
        ))
    }

    getQuestionLevel() {
        return this.questionLevel.toLowerCase()
    }

    updateSkipLogic(store, skipLogic) {
        console.log(`updateSkipLogic ${store.surveyToken}`)
        store.http.put(`/api/survey/${store.survey.id}/pageQuestion/${this.id}`, JSON.stringify({
            skipLogic: skipLogic.map((sl) => sl.toJSON()),
        }), store.surveyToken)
            .then((response) => {
                this.skipLogic = skipLogic
                store.addNotification({ body: 'SkipLogic updated', level: 'success' })
            })
            .catch((e) => {
                console.log(e)
                store.handleError({ body: 'Error updating SkipLogic', level: 'error' })
            })
    }

    updateSmartGrid(store, smartGrid) {
        console.log(`updateSmartGrid ${store.surveyToken}`)
        store.http.put(`/api/survey/${store.survey.id}/pageQuestion/${this.id}`, JSON.stringify({
            optionSetY: smartGrid.toJSON(),
        }), store.surveyToken)
            .then((response) => {
                this.optionSetY = smartGrid
                store.addNotification({ body: 'SmartGrid updated', level: 'success' })
            })
            .catch((e) => {
                console.log(e)
                store.handleError({ body: 'Error updating SmartGrid', level: 'error' })
            })
    }

    refresh(store) {
        store.http.get(`/api/question/${this.questionId}/7`)
            .then((response) => {
                console.log(response)
                const { question } = response
                switch (question.questionTypeCode) {
                    case 'CH':
                        this.question = new MultipleChoiceQuestion(question)
                        break
                    case 'CO':
                        this.question = new CountryListQuestion(question)
                        break
                    case 'I':
                        this.question = new NumericQuestion(question)
                        break
                    case 'OD':
                        this.question = new PriorityQuestion(question)
                        break
                    case 'PC':
                        this.question = new PercentageQuestion(question)
                        break
                    case 'RD':
                        this.question = new SingleChoiceQuestion(question)
                        break
                    case 'RT':
                        this.question = new RatingQuestion(question)
                        break
                    case 'SB':
                        this.question = new DropdownListQuestion(question)
                        break
                    case 'ST':
                        this.question = new StaticText(question)
                        break
                    case 'TA':
                        this.question = new TextAreaQuestion(question)
                        break
                    case 'TB':
                        this.question = new GridQuestion(question)
                        break
                    case 'SG':
                        this.question = new SmartGridQuestion(question)
                        break
                    case 'TX':
                        this.question = new TextBoxQuestion(question)
                        break
                    case 'UV':
                        this.question = new UrlVariableQuestion(question)
                        break
                    case 'TL':
                        this.question = new TextBoxListQuestion(question)
                        break
                    case 'DT':
                        this.question = new DateQuestion(question)
                        break
                    case 'FM':
                        this.question = new FormQuestion(question)
                        break
                    case 'NF':
                        this.question = new NumberFormQuestion(question)
                        break
                    default:
                        this.question = null
                        break
                }
                this.isEditable = response.editMode === 'Edit' || response.editMode === 'Clone'
                this.modifiable = true
                this.deletable = this.setDeletable(store)
                console.log(JSON.stringify(this.question, null, 4))
            })
            .catch((e) => {
                console.log(e)
            })
    }

    setDeletable(store) {
        const that = this
        const { survey } = store
        let url = `/qlist/mandatory/${this.questionId}`
        url += `?portfolioId=${survey.portfolioId}`
        url += `?surveyType=${survey.metadata.taxonomy.surveyType}`
        url += `&respondentType=${survey.metadata.taxonomy.respondentType}`
        url += `&productType=${survey.metadata.productInfo.industrySpecificInfo.productType}`
        store.http.get(url)
            .then((response) => {
                that.deletable = !response.mandatory || that.isRecommended
            })
            .catch((e) => console.log)
    }

    async update(options) {
        console.log(options)
        console.log(this.currentOptions)
        console.log(`updateQuestion ${this.id}`)
        this.currentOptions = options
        if (!this.currentOptions.isHidden) {
            this.populateFrom = undefined
            this.hasPopulateFrom = false
        }
        return PageQuestionAPI.update(this)
    }

    async updateQuestionMapping(store, body) {
        const { survey, surveyToken } = store
        await store.http.put(
            `/api/survey/${survey.id}/pageQuestion/${this.id}/populateFrom`,
            JSON.stringify(body),
            surveyToken,
        )
    }

    getCurrentOptions() {
        return {
            isMandatory: this.isMandatory,
            isHidden: this.isHidden,
            isLocked: this.isLocked,
            // parentId: this.parentId,
            questionId: this.questionId,
            questionLevel: this.questionLevel,
            isEditable: this.canBeEdited,
            isAiCodingDisabled: this.isAiCodingDisabled,
        }
    }

    toJSON() {
        return {
            id: this.id,
            questionId: this.questionId,
            priority: this.priority,
            isMandatory: this.isMandatory,
            isHidden: this.isHidden,
            isLocked: this.isLocked,
            parentId: this.parentId,
            hasPrepopulatedData: this.hasPrepopulatedData,
            questionLevel: this.questionLevel,
            optionSetY: this.optionSetY,
            skipLogic: this.skipLogic && this.skipLogic.length > 0 ? this.skipLogic : undefined,
            isEditable: this.canBeEdited,
            isAiCodingDisabled: this.isAiCodingDisabled,
        }
    }
}

class Page {
    @observable isRandomized = false

    @observable isHidden = false

    @observable priority

    @observable questions

    @observable redirectUrlSettings = {}

    @observable redirectUrlChecked

    @observable pageChart

    @observable template
    @observable isLocked

    @computed get hasMandatoryQuestions() {
        return this.questions.reduce((mandatory, question) => mandatory || !question.deletable, false)
    }

    @computed get hasShowageSkipLogic() {
        return createTransformer((store) => !!this.getShowPageLogic(store.skipLogic))
    }

    constructor({
        id,
        pageType,
        priority,
        questions = [],
        title,
        isRandomized = false,
        isHidden = false,
        content,
        redirectUrlSettings = {},
        pageChart,
        template,
        isLocked,
    }) {
        makeObservable(this)
        this.id = id
        this.pageType = pageType
        this.priority = priority
        this.redirectUrlSettings = redirectUrlSettings
        this.redirectUrlChecked = !!redirectUrlSettings.redirectUrl
        this.pageChart = pageChart
        this.template = template
        this.isLocked = isLocked

        if (this.pageType === surveyPageTypes.StandardPage) {
            this.questions = questions.map((question) => (
                question instanceof PageQuestion ? question : new PageQuestion(question)
            ))
        }

        this.title = title
        this.isRandomized = isRandomized
        this.isHidden = isHidden

        if (this.pageType !== surveyPageTypes.StandardPage) {
            this.content = content instanceof ExploriMap
                ? content : new ExploriMap(Object.entries(content || {})
                    .map(([locale, langText]) => (
                        [locale, (langText instanceof LangText ? langText : new LangText(langText))]
                    )))
        }
    }

    getSkipLogic(store) {
        const { survey } = store
        const { skipLogic } = survey
        return skipLogic.filter((logic) => (
            (
                logic.action.kind === 'PageToPage'
                && logic.trigger.condition.pageId === this.id
            )
        ))
    }

    getShowPageLogic(skipLogic) {
        return skipLogic.find(({ action }) => (
            action.kind === 'ShowPage'
            && action.pageId === this.id
        ))
    }

    getSkipToPage(store) {
        console.log(`getSkipToPage ${this.id}`)
        const { survey } = store
        const { skipLogic } = survey
        const pageQuestionIds = this.questions.map((question) => question.id)
        const skipToPageLogic = skipLogic.filter((logic) => logic.action.kind === 'SkipToPage')

        const skipToPage = {
            hasSkipToPage: false,
            skipToPageIsValid: true,
        }

        const quotaLogic = skipToPageLogic.filter((logic) => (
            logic.triggerGroups
                && R.pathOr([], ['triggerGroups', 0, 'children', 0, 'triggers'])(logic).find(({ condition }) => condition.kind === 'Quota')
        ))

        console.log(`getSkipToPage ${JSON.stringify(quotaLogic, null, 4)}`)

        quotaLogic.reduce((result, logic) => {
            const trigger = logic.triggerGroups[0].children[0].triggers.find(({ condition }) => condition.kind === 'CurrentPage')
            if (trigger && trigger.condition.pageId === this.id) {
                result.hasSkipToPage = true
            }
            return result
        }, skipToPage)

        return skipToPageLogic.reduce((result, logic) => {
            const { condition } = logic.triggerGroups[0]
                ? logic.triggerGroups[0].children[0].triggers[0]
                : logic.trigger

            if (pageQuestionIds.indexOf(condition.pageQuestionId) > -1) {
                result.hasSkipToPage = true
                result.skipToPageIsValid = result.skipToPageIsValid && survey.pages.find((page) => page.id === logic.action.pageId && page.priority > this.priority)
                if (result.skipToPageIsValid) {
                    console.log('skiplogicValidate got here')
                    const pageQuestion = this.questions.find((question) => question.id === condition.pageQuestionId)
                    console.log(`skiplogicValidate has pageQuestion ${!!pageQuestion}`)
                    console.log(`skiplogicValidate sets ${condition.set1}, ${condition.set2}`)
                    if (condition.kind === 'QuestionQuota') {
                        result.skipToPageIsValid = pageQuestion
                    } else {
                        result.skipToPageIsValid = pageQuestion && pageQuestion.question
                        && pageQuestion.question.hasOptions(condition.set1, condition.set2, pageQuestion)
                    }
                }
            }

            return result
        }, skipToPage)
    }

    update(store, survey) {
        const content = JSON.stringify({
            isRandomized: this.isRandomized,
            title: this.title,
            isHidden: this.isHidden,
            template: this.template,
            pageChart: this.pageChart,
            isLocked: this.isLocked,
        })
        store.http.put(`/api/survey/${survey.id}/page/${this.id}`, content, store.surveyToken)
            .then((response) => {
                console.log(`done ${response}`)
                if (this.isHidden) {
                    store.survey.refreshSkipLogic(store)
                }
                store.addNotification({ body: 'Page updated', level: 'success' })
            })
            .catch((e) => {
                console.log(e)
                store.handleError({ body: `Error updating page: ${e.message}`, level: 'error' })
            })
    }

    moveQuestion = ({
        store,
        question,
        new_index,
        pageId,
        soft,
    }) => {
        const that = this
        const { survey } = store
        let response = null
        console.log(`moving question ${question.id} to index ${new_index}`)
        const questionBackup = that.questions.slice()

        const id = pageId || this.id
        const endpoint = `/api/survey/${survey.id}/page/${id}/pageQuestion/${question.id}/move`
        const body = JSON.stringify({
            toPageId: that.id,
            toPos: new_index,
        })
        if (soft) {
            that.questions.splice((new_index - 1), 0, question)
            response = {
                body,
                questionBackup,
                endpoint,
                targetPage: this,
            }
        } else {
            withRollback({
                target: that,
                fields: ['questions'],
                store,
                successBody: 'Question moved',
                errorBody: (e) => `Failed to move question: ${e.message}`,
                action: async () => {
                    that.questions.splice((new_index - 1), 0, question)
                    const res = await store.http.put(endpoint, body, store.surveyToken)
                    console.log(`done ${res}`)
                    survey.refreshSkipLogic(store)
                },
            })
        }
        return response
    }

    saveMove = (store, body, questionBackup, endpoint) => {
        const { survey } = store
        return store.http.put(endpoint, body, store.surveyToken)
            .then((res) => {
                console.log(`done ${res}`)
                survey.refreshSkipLogic(store)
                store.addNotification({ body: 'Question moved', level: 'success' })
                return true
            }).catch((e) => {
                console.log(e)
                store.handleError({ body: `Failed to move question: ${e.message}`, level: 'error' })
                this.questions = questionBackup
                return false
            })
    }

    removeQuestion = async ({
        store,
        question,
        index,
        soft,
    }) => {
        const { survey } = store
        const that = this
        const newArr = []
        that.questions.forEach((question) => {
            newArr.push(question)
        })
        newArr.splice(index, 1)

        console.log(`removeQuestion ${index} from page ${that.id}`)
        const endpoint = `/api/survey/${survey.id}/page/${that.id}/pageQuestion/${question.id}`
        if (soft) {
            that.questions = newArr
            return
        }

        await withRollback({
            target: that,
            fields: ['questions'],
            store,
            successBody: 'Question deleted',
            errorBody: (e) => `Failed to delete question: ${e.message}`,
            action: async () => {
                that.questions = newArr
                for (const skipLogic of question.filterSkipLogic(store)) {
                    await skipLogic.remove(store)
                }
                const res = await store.http.delete(endpoint, store.surveyToken)
                console.log(`done ${res}`)
                survey.num_questions -= 1
                question.status = 'deleted'
                const questionIndex = survey.pageQuestions.findIndex((pq) => pq === question.questionId.toLowerCase())
                if (questionIndex > -1) {
                    survey.pageQuestions.splice(questionIndex, 1)
                }
                survey.refreshSkipLogic(store)
            },
        })
    }

    copyQuestion = (question, store) => {
        const questionCopy = question.clone()
        questionCopy.question = null
        questionCopy.id = 'tempCopy'
        questionCopy.copying = true
        const body = JSON.stringify({
            pageQuestionId: question.id,
        })
        const index = (this.questions.findIndex((q) => q.id === question.id) + 1)
        return withRollback({
            target: this,
            fields: ['questions'],
            store,
            successBody: 'Question copied',
            errorBody: (e) => `Error copying question: ${e.message}`,
            action: async () => {
                this.questions.splice(index, 0, questionCopy)
                const endpoint = `/api/survey/${store.survey.id}/page/${this.id}/pageQuestion/copy`
                const response = await store.http.post(endpoint, body, store.surveyToken)
                questionCopy.id = response.newPageQuestionId
                questionCopy.skipLogic = []
                questionCopy.questionId = response.newQuestionId
                questionCopy.questionLevel = 'Product'
                const { newQuestion } = response
                switch (newQuestion.questionTypeCode) {
                    case 'CH':
                        questionCopy.question = new MultipleChoiceQuestion(newQuestion)
                        break
                    case 'CO':
                        questionCopy.question = new CountryListQuestion(newQuestion)
                        break
                    case 'I':
                        questionCopy.question = new NumericQuestion(newQuestion)
                        break
                    case 'OD':
                        questionCopy.question = new PriorityQuestion(newQuestion)
                        break
                    case 'PC':
                        questionCopy.question = new PercentageQuestion(newQuestion)
                        break
                    case 'RD':
                        questionCopy.question = new SingleChoiceQuestion(newQuestion)
                        break
                    case 'RT':
                        questionCopy.question = new RatingQuestion(newQuestion)
                        break
                    case 'SB':
                        questionCopy.question = new DropdownListQuestion(newQuestion)
                        break
                    case 'ST':
                        questionCopy.question = new StaticText(newQuestion)
                        break
                    case 'TA':
                        questionCopy.question = new TextAreaQuestion(newQuestion)
                        break
                    case 'TB':
                        questionCopy.question = new GridQuestion(newQuestion)
                        break
                    case 'TX':
                        questionCopy.question = new TextBoxQuestion(newQuestion)
                        break
                    case 'UV':
                        questionCopy.question = new UrlVariableQuestion(newQuestion)
                        break
                    case 'TL':
                        questionCopy.question = new TextBoxListQuestion(newQuestion)
                        break
                    case 'DT':
                        questionCopy.question = new DateQuestion(newQuestion)
                        break
                    default:
                        questionCopy.question = null
                        break
                }
                // questionCopy.refresh(store)
                questionCopy.copying = false
                questionCopy.isEditable = true
                questionCopy.modifiable = true
                questionCopy.deletable = questionCopy.setDeletable(store)
                questionCopy.canBeEdited = true
            },
        })
    }

    @action
    addQuestion = async ({
        question,
        survey,
        position,
        store,
        smartRows,
        isHidden = false,
    }, forceId) => {
        console.log(`add question ${question.questionId} to page ${this.id}`)
        const that = this
        const pos = (typeof position === 'number') ? (position + 1) : this.questions.length + 1
        const dataImportQuestion = R.clone(question.isDataImport)

        let level = 'Product'
        if (question.oid === 1 || question.clientId === 1) {
            level = 'Explori'
        } else if (question.oid < 6) {
            level = 'Client'
        }

        if (typeof position === 'number') {
            console.log(`question is ${question} ${question instanceof PageQuestion}`)

            // if question has a parentId and it's not present in the survey
            if (question.parentId
                && !survey.pageQuestions.includes(question.parentId.toLowerCase())) {
                return false
            }

            if (!(question instanceof PageQuestion)) {
                console.log(`not a pageQuestion ${question.isMandatory}`)
                question = new PageQuestion({
                    questionId: question.questionId,
                    priority: pos,
                    questionLevel: level,
                    isMandatory: (question.mandatory || question.isMandatory),
                    isAiCodingDisabled: Boolean(question.isAiCodingDisabled),
                    isHidden: !!dataImportQuestion || isHidden,
                    parentId: question.parentId ? question.parentId.toLowerCase() : undefined,
                    isEditable: true,
                })
            }
            const parent = question
                .previousPageQuestions(({ store, currentPriority: this.priority }))
                .find((q) => q.qid.toLowerCase() === question.parentId)

            if (!parent && question.parentId) {
                return false
            }

            if (parent) question.parentId = parent.value
        }

        const endpoint = `/api/survey/${survey.id}/page/${that.id}/pageQuestion`
        const body = {
            questionIdReference: question.questionId,
            atPos: pos,
            isMandatory: Boolean(question.isMandatory || question.mandatory),
            isHidden: !!dataImportQuestion || isHidden,
            questionLevel: level,
            parentId: question.parentId,
            qlistInherit: !!question.parentId,
            isEditable: true,
            isAiCodingDisabled: Boolean(question.isAiCodingDisabled),
        }

        if (question.questionTypeCode === 'SG' && smartRows) {
            body.optionSetY = smartRows
        }

        if (forceId) {
            body.id = question.id
        }

        await withRollback({
            target: this,
            fields: ['questions'],
            store,
            successBody: 'Question added to page',
            errorBody: (e) => `Error adding question to page: ${e.message}`,
            action: async () => {
                if (typeof position === 'number') {
                    this.questions.splice(position, 0, question)
                }

                const res = await store.http.post(endpoint, JSON.stringify(body), store.surveyToken)

                console.log(`added question to page ${JSON.stringify(res, null, 4)}`)

                const { newPageQuestionId } = res
                if (newPageQuestionId !== undefined) store.iframeRefs.set(newPageQuestionId, {})

                console.log(`${position}, ${(typeof position)}`)
                if ((typeof position !== 'number')) {
                    console.log('PUSHING')
                    const pageQuestion = new PageQuestion({
                        id: res.newPageQuestionId,
                        questionId: question.questionId,
                        priority: pos,
                        isMandatory: body.isMandatory,
                        isHidden: body.isHidden,
                        questionLevel: body.questionLevel,
                        parentId: body.parentId,
                        optionSetY: body.optionSetY,
                        isRecommended: question.isRecommended,
                        isDataImport: question.isDataImport,
                        qlistInherit: body.qlistInherit,
                    })
                    survey.pageQuestions.push(pageQuestion.questionId.toLowerCase())
                    that.questions.push(pageQuestion)
                    pageQuestion.refresh(store)
                } else if (res.newPageQuestionId) {
                    question.id = res.newPageQuestionId
                    question.qlistInherit = body.qlistInherit
                    question.isHidden = body.isHidden
                    survey.pageQuestions.push(question.questionId.toLowerCase())
                    question.refresh(store)
                }
                survey.num_questions += 1
            },
        })
        return true
    }

    toJSON() {
        return {
            id: this.id,
            pageType: this.pageType,
            priority: this.priority,
            questions: this.questions
                ? this.questions.map((question) => question.toJSON()) : undefined,
            title: this.title,
            isRandomized: this.isRandomized,
            content: this.content,
            redirectUrlSettings: this.redirectUrlSettings,
            isLocked: this.isLocked,
        }
    }
}

export {
    Page,
    PageQuestion,
    LangText,
    ExploriMap,
}
